import { ChangeEvent, DragEvent, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import Icon from '../../../../components/icon'
import MsiDatagridAuto from '../../../../components/custom/MsiDatagridAuto'
import apiUrl from '../../../../configs/constant'
import { useToast } from '../../../../components/ToastProvider'

type Summary = {
  success_count: number
  duplicate_count: number
  failed_count: number
  total_count: number
}

type AccountInfo = {
  no_rekening?: string
  nama?: string
  mata_uang?: string
  periode?: string
}

const emptySummary: Summary = {
  success_count: 0,
  duplicate_count: 0,
  failed_count: 0,
  total_count: 0
}

export default function Index() {
  const userData = JSON.parse(localStorage.getItem('user') || '{}')
  const token = userData.token
  const { showToast } = useToast()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [hasResult, setHasResult] = useState(false)
  const [listData, setListData] = useState<any[]>([])
  const [summary, setSummary] = useState<Summary>(emptySummary)
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [detectedFormat, setDetectedFormat] = useState('')

  const selectFile = (selectedFile?: File) => {
    if (!selectedFile) return

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      showToast('Pilih file CSV hasil ekspor rekening koran BCA.', 'error')
      return
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      showToast('Ukuran file CSV maksimal 10 MB.', 'error')
      return
    }

    setFile(selectedFile)
    setHasResult(false)
    setListData([])
    setSummary(emptySummary)
    setAccount(null)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0])
    event.target.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    selectFile(event.dataTransfer.files?.[0])
  }

  const handleImport = async () => {
    if (!file || isImporting) return

    const formData = new FormData()
    formData.append('file', file)
    setIsImporting(true)

    try {
      const response = await axios.post(
        apiUrl.apiUrl + '/acc/rekening-koran-bca/import',
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const result = response.data

      setListData(result.data || [])
      setSummary({
        success_count: result.success_count || 0,
        duplicate_count: result.duplicate_count || 0,
        failed_count: result.failed_count || 0,
        total_count: result.total_count || 0
      })
      setAccount(result.account || null)
      setDetectedFormat(result.format || '')
      setHasResult(true)
      showToast(result.message, result.failed_count > 0 ? 'warning' : 'success')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Import gagal. Silakan coba kembali.'
      showToast(message, 'error')
    } finally {
      setIsImporting(false)
    }
  }

  const summaryCards = [
    { label: 'Total transaksi', value: summary.total_count, color: '#1565c0', background: '#e3f2fd', icon: 'mdi:file-document-outline' },
    { label: 'Data baru', value: summary.success_count, color: '#2e7d32', background: '#e8f5e9', icon: 'mdi:check-circle-outline' },
    { label: 'Duplikat', value: summary.duplicate_count, color: '#ed6c02', background: '#fff3e0', icon: 'mdi:content-copy' },
    { label: 'Gagal', value: summary.failed_count, color: '#d32f2f', background: '#ffebee', icon: 'mdi:alert-circle-outline' }
  ]

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper className='content-block dx-card' sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}
          >
            <Box>
              <Typography variant='h5' fontWeight={700}>Import Rekening Koran BCA</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                Impor CSV BCA corporate atau personal dengan pemeriksaan duplikat otomatis.
              </Typography>
            </Box>
            <Button
              variant='outlined'
              color='inherit'
              startIcon={<Icon icon='mdi:arrow-left' />}
              onClick={() => navigate(-1)}
            >
              Kembali
            </Button>
          </Box>

          <Divider />

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Alert severity='info' sx={{ mb: 3 }}>
              Gunakan file <strong>.CSV</strong> asli dari KlikBCA/KlikBCA Bisnis. Tanggal tanpa tahun dan transaksi berstatus PEND akan dikenali otomatis dari periode/keterangan file.
            </Alert>

            <Box
              onDragOver={(event) => {
                event.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => !isImporting && fileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : file ? 'success.main' : 'divider',
                backgroundColor: isDragging ? 'action.hover' : file ? '#f7fcf8' : 'background.default',
                borderRadius: 2,
                p: { xs: 3, md: 5 },
                textAlign: 'center',
                cursor: isImporting ? 'default' : 'pointer',
                transition: 'all 160ms ease'
              }}
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv,text/csv'
                hidden
                onChange={handleFileChange}
              />

              <Icon
                icon={file ? 'mdi:file-check-outline' : 'mdi:cloud-upload-outline'}
                fontSize={52}
                color={file ? '#2e7d32' : '#1976d2'}
              />
              <Typography variant='h6' fontWeight={600} sx={{ mt: 1 }}>
                {file ? file.name : 'Tarik file CSV ke sini'}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                {file
                  ? `${(file.size / 1024).toLocaleString('id-ID', { maximumFractionDigits: 1 })} KB · klik untuk mengganti file`
                  : 'atau klik untuk memilih file · maksimal 10 MB'}
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='flex-end' sx={{ mt: 2.5 }}>
              {file && (
                <Button
                  variant='text'
                  color='inherit'
                  disabled={isImporting}
                  onClick={() => {
                    setFile(null)
                    setHasResult(false)
                    setListData([])
                  }}
                >
                  Hapus file
                </Button>
              )}
              <Button
                variant='contained'
                size='large'
                disabled={!file || isImporting}
                startIcon={isImporting ? <CircularProgress size={18} color='inherit' /> : <Icon icon='mdi:database-import-outline' />}
                onClick={handleImport}
                sx={{ minWidth: 180 }}
              >
                {isImporting ? 'Mengimpor...' : 'Import sekarang'}
              </Button>
            </Stack>

            {hasResult && (
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                  <Typography variant='h6' fontWeight={700}>Hasil import</Typography>
                  {detectedFormat && <Chip size='small' color='primary' variant='outlined' label={`BCA ${detectedFormat}`} />}
                  {account?.no_rekening && <Chip size='small' variant='outlined' label={`Rek. ${account.no_rekening}`} />}
                  {account?.nama && <Chip size='small' variant='outlined' label={account.nama} />}
                  {account?.periode && <Chip size='small' variant='outlined' label={account.periode} />}
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {summaryCards.map(item => (
                    <Grid item xs={6} md={3} key={item.label}>
                      <Card variant='outlined' sx={{ height: '100%', borderColor: item.color, backgroundColor: item.background }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, '&:last-child': { pb: 2 } }}>
                          <Icon icon={item.icon} fontSize={30} color={item.color} />
                          <Box>
                            <Typography variant='body2' color='text.secondary'>{item.label}</Typography>
                            <Typography variant='h5' fontWeight={700} color={item.color}>{item.value}</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {listData.length > 0 && (
                  <MsiDatagridAuto
                    array={listData}
                    hiddenColumns={['id']}
                    convertRupiah={['jumlah', 'saldo']}
                    customRender={[
                      {
                        key: 'status',
                        renderCell: (cellData: any) => (
                          <Chip
                            size='small'
                            label={cellData.value}
                            color={cellData.value === 'Berhasil' ? 'success' : cellData.value === 'Duplikat' ? 'warning' : 'error'}
                            variant='outlined'
                          />
                        )
                      }
                    ]}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Paper>
      </Grid>
    </Grid>
  )
}
